import React, { useEffect, useRef } from 'react';
import { Box, Drawer, Typography, Divider, Button, Chip, LinearProgress } from '@mui/material';
import { AutoAwesome, OpenInNew, CheckCircle } from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState } from '../../store/store';
import { clearAgentProgress } from '../../store/slices/agentProgressSlice';
import AgentStepTimeline from './AgentStepTimeline';

const DEFAULT_PHASE_LABELS: Record<string, string> = {
  plan: 'Generating refactor plan…',
  pr:   'Raising pull request…',
  fix:  'Generating fixes…',
};

const DEFAULT_PHASE_DONE_LABELS: Record<string, string> = {
  plan: 'Plan ready for review',
  pr:   'Pull request opened',
  fix:  'Pull request opened',
};

interface Props {
  onComplete?: () => void;
  phaseLabels?: Record<string, string>;
  phaseDoneLabels?: Record<string, string>;
}

const AgentProgressDrawer: React.FC<Props> = ({ onComplete, phaseLabels, phaseDoneLabels }) => {
  const PHASE_LABELS = { ...DEFAULT_PHASE_LABELS, ...phaseLabels };
  const PHASE_DONE_LABELS = { ...DEFAULT_PHASE_DONE_LABELS, ...phaseDoneLabels };
  const dispatch = useDispatch();
  const progress = useSelector((s: RootState) => s.agentProgress.active);
  const feedRef = useRef<HTMLDivElement>(null);

  // Auto-scroll feed to bottom as messages arrive
  useEffect(() => {
    if (feedRef.current) {
      feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }
  }, [progress?.feed.length]);

  // Auto-transition when complete
  useEffect(() => {
    if (!progress?.isComplete) return;
    const timer = setTimeout(() => {
      onComplete?.();
      dispatch(clearAgentProgress());
    }, 2000);
    return () => clearTimeout(timer);
  }, [progress?.isComplete, dispatch, onComplete]);

  const open = Boolean(progress && !progress.isFailed ? true : progress?.isFailed ? true : false);

  if (!progress) return null;

  const runningStep = progress.steps.find((s) => s.status === 'running');
  const doneCount   = progress.steps.filter((s) => s.status === 'done').length;
  const totalSteps  = progress.steps.length;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => dispatch(clearAgentProgress())}
      PaperProps={{ sx: { width: 360, p: 0 } }}
    >
      {/* Header */}
      <Box sx={{ px: 2.5, py: 2, bgcolor: 'primary.main', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <AutoAwesome sx={{ fontSize: 18 }} />
          <Typography variant="subtitle2" fontWeight={700}>
            {progress.isComplete ? PHASE_DONE_LABELS[progress.phase] : PHASE_LABELS[progress.phase]}
          </Typography>
          {progress.isComplete && <CheckCircle sx={{ fontSize: 16, ml: 'auto' }} />}
        </Box>
        {!progress.isComplete && (
          <Typography variant="caption" sx={{ opacity: 0.85 }}>
            {runningStep?.message ?? 'Working…'}
          </Typography>
        )}
      </Box>

      {/* Progress bar */}
      {!progress.isComplete && !progress.isFailed && totalSteps > 0 && (
        <LinearProgress
          variant="determinate"
          value={Math.round((doneCount / Math.max(totalSteps, 1)) * 100)}
          sx={{ height: 3 }}
        />
      )}

      <Box sx={{ px: 2.5, py: 2.5, flex: 1, overflow: 'auto' }}>
        {/* Step timeline */}
        <AgentStepTimeline steps={progress.steps} />

        {/* PR complete CTA */}
        {progress.isComplete && (progress.phase === 'pr' || progress.phase === 'fix') && progress.prUrl && (
          <Box sx={{ mt: 3, p: 2, bgcolor: '#f0fdf4', border: '1px solid', borderColor: 'success.200', borderRadius: 2 }}>
            <Typography variant="body2" fontWeight={700} color="success.dark" sx={{ mb: 1 }}>
              PR #{progress.prNumber} is open
            </Typography>
            <Button
              variant="contained" size="small" color="success" disableElevation
              startIcon={<OpenInNew />}
              href={progress.prUrl} target="_blank" rel="noopener noreferrer"
            >
              View pull request
            </Button>
          </Box>
        )}

        {/* Message feed */}
        {progress.feed.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" fontWeight={700} color="text.disabled" display="block" sx={{ mb: 1 }}>
              ACTIVITY
            </Typography>
            <Box
              ref={feedRef}
              sx={{ maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}
            >
              {progress.feed.map((msg, i) => (
                <Typography key={i} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  → {msg}
                </Typography>
              ))}
            </Box>
          </>
        )}
      </Box>

      {/* Dismiss on failure */}
      {progress.isFailed && (
        <Box sx={{ px: 2.5, pb: 2 }}>
          <Chip
            label="Dismiss"
            variant="outlined"
            size="small"
            onClick={() => dispatch(clearAgentProgress())}
          />
        </Box>
      )}
    </Drawer>
  );
};

export default AgentProgressDrawer;
